import { useNavigate } from 'react-router-dom';
import { formatPrice, localPrice } from '@/lib/consultationPrice';

/*
 * Третья плашка витрины: личная консультация.
 *
 * Намеренно НЕ похожа на карточки тарифов. Подписка и личная работа с
 * астрологом — разные вещи, и если консультацию оформить как третий
 * уровень подписки, человек будет сравнивать её с ЛАЙТ и ПРО по цене,
 * а не выбирать между «сервис считает сам» и «Светлана смотрит мою
 * карту». Отсюда отличия, которые видно сразу: белый фон без заливки
 * тарифов, пунктирная рамка, надпись «личная работа» вместо бейджа
 * тарифа, тире в списке вместо галочек и контурная кнопка.
 *
 * Кнопка ведёт на /consultation, а НЕ в оплату: услуга дорогая, про неё
 * сначала читают.
 *
 * Один компонент на две витрины — главную (PricingBlock) и /pricing.
 * compact — вариант для главной, где вся витрина набрана мелко.
 */

// Первые четыре из шести карточек «с чем приходят» на /consultation —
// только названия, без описаний.
const TOPICS = ['Трудный период', 'Отношения', 'Деньги и дело', 'Время для шага'];

// Цена — из общего правила (lib/consultationPrice.js), того же, по
// которому живут /consultation и касса. На время акции здесь сама собой
// оказывается акционная сумма: витрина не должна обещать одно, а страница
// заказа показывать другое. Сети карточка не трогает — она стоит на
// витринах пачкой, и запрос от каждой был бы лишним.

const TITLE = 'ЛИЧНАЯ КОНСУЛЬТАЦИЯ';
const SUBTITLE = 'Видеоразбор, который останется у тебя навсегда';
const CTA_NOTE = 'Не подписка — твой личный видеоразбор от астролога';

export default function ConsultationCard({ compact = false }) {
  const navigate = useNavigate();
  const PRICE = formatPrice(localPrice().amount);

  const s = compact
    ? {
        card: 'p-2 md:p-2.5',
        label: 'text-[9px]',
        title: 'text-sm md:text-base',
        subtitle: 'text-[11px]',
        list: 'text-xs',
        price: 'text-lg md:text-xl',
        unit: 'text-xs',
        button: 'h-7 text-xs',
        note: 'text-[10px]',
      }
    : {
        card: 'p-4',
        label: 'text-[10px]',
        title: 'text-base',
        subtitle: 'text-xs',
        list: 'text-xs',
        price: 'text-2xl',
        unit: 'text-sm',
        button: 'h-10 text-sm',
        note: 'text-xs',
      };

  return (
    <div
      className={`flex flex-col rounded-xl border-2 border-dashed border-slate-300 bg-white shadow-sm ${s.card}`}
      data-testid="pricing-card-consultation"
    >
      <p className={`${s.label} font-bold uppercase tracking-widest text-slate-400 mb-1`}>
        Личная работа
      </p>
      <h3 className={`${s.title} font-bold text-slate-900 leading-tight`}>{TITLE}</h3>
      <p className={`${s.subtitle} text-slate-600 leading-snug mt-0.5 mb-1.5`}>{SUBTITLE}</p>

      <ul className={`${s.list} space-y-0 text-slate-700 mb-1.5 flex-1 leading-tight`}>
        {TOPICS.map((t) => (
          <li key={t} className="flex items-start gap-1.5 leading-snug">
            <span className="text-slate-400 flex-shrink-0">—</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>

      <div className="mb-1">
        <span className={`${s.price} font-black text-slate-900 leading-tight`}>{PRICE}</span>
        <span className={`${s.unit} font-semibold text-slate-500 ml-1`}>разово</span>
      </div>

      {/* Подпись — НАД кнопкой: под кнопкой она поднимала «Подробнее» выше
          кнопок соседних плашек. Кнопка — последняя в карточке, mt-auto
          прижимает её к низу, как у «Бесплатно», «Разовой» и ПРО. */}
      <p className={`${s.note} text-slate-500 leading-snug mb-2 text-center`} data-testid="pricing-consultation-note">{CTA_NOTE}</p>
      <button
        type="button"
        onClick={() => navigate('/consultation')}
        className={`mt-auto w-full ${s.button} rounded-lg border-2 border-slate-700 text-slate-800 font-bold hover:bg-slate-800 hover:text-white transition-colors`}
        data-testid="pricing-cta-consultation"
      >
        Подробнее
      </button>
    </div>
  );
}
